import { createContext, useEffect, useRef, useState } from 'react';
import { StakeOptionFilter, TableFilter } from '../../../services/lobby/lobbyServicesTypes';
import { PokerCategory } from '../../../services/common/types/pokerCategory';
import { TableType } from '../../../services/common/types/tableType';
import { GameValue } from '../../../features/lobby/filters/filters';
import { useDispatch, useSelector } from 'react-redux';
import { ReducerState } from '../../../services/common/lib/reducerState';
import { isEqual } from 'lodash';
import useUserLobbyFilters from '../../../services/lobby/mutations/useUserLobbyFilters';
import { StakeName, stakesOptions } from '../../../features/lobby/constants/filter-options';
import { BettingStructure } from '../../../services/common/types/bettingStructure';
import { authActionCreator, AuthEvents } from '../../../services/auth/authActions';

export interface LobbyContextProps {
  filters: TableFilter;
  handleCategory: (option?: PokerCategory) => void;
  handleTableType: (option?: TableType) => void;
  handleRangeChange: (minValue: number, maxValue: number) => void;
  handleFullTables: () => void;
  handleEmptyTables: () => void;
  handleNameSearch: (name: string) => void;
  handleTableSize: (size: number) => void;
  handleStakes: (gameStake: StakeName) => void;
  handleGames: (game: GameValue) => void;
  handleResetFilters: () => void;
  handleGameType: (gameType: BettingStructure) => void;
  handleSaveFilters: () => void;
  handleCloseWithoutSaving: () => void;
}

const LobbyFiltersContext = createContext<LobbyContextProps | null>(null);

export const LobbyFiltersContextProvider = ({ children }: { children: React.ReactNode }) => {
  const userLobbyFilters = useSelector((state: ReducerState) => state.auth?.lobbyFilters);
  const dispatch = useDispatch();
  const lobbyFiltersRef = useRef(userLobbyFilters);

  useEffect(() => {
    lobbyFiltersRef.current = userLobbyFilters;
  }, [userLobbyFilters]);

  const INITIAL_FILTERS: TableFilter = {
    tableType: undefined,
    category: PokerCategory.ALL,
    fullTables: undefined,
    emptyTables: undefined,
    minBuyIn: undefined,
    maxBuyIn: undefined,
    name: undefined,
    maxPlayers: undefined,
    stakeOptions: [],
    games: [],
    gameTypes: [],
  };

  const { mutate: saveLobbyFilters } = useUserLobbyFilters();
  const [filters, setFilters] = useState<TableFilter>(
    userLobbyFilters ? { ...userLobbyFilters, category: PokerCategory.ALL } : INITIAL_FILTERS,
  );

  const handleSaveFilters = () => {
    dispatch(authActionCreator(AuthEvents.UPDATE_LOBBY_FILTERS, filters));
    saveLobbyFilters(filters);
  };

  const handleCloseWithoutSaving = () => {
    setFilters(lobbyFiltersRef.current || INITIAL_FILTERS);
  };

  const handleCategory = (option?: PokerCategory) => {
    const category = filters.category === option ? undefined : option;

    // TODO There is no custom category or chinese yet
    if (category === PokerCategory.CUSTOM || category === PokerCategory.CHINESE) return;

    dispatch(authActionCreator(AuthEvents.UPDATE_LOBBY_FILTERS, { category }));

    setFilters((prev) => ({
      ...prev,
      games: [],
      category: category,
    }));
  };

  const handleTableType = (option?: TableType) => {
    const tableType = filters.tableType === option ? undefined : option;
    setFilters((prev) => ({
      ...prev,
      tableType,
    }));
  };

  const handleRangeChange = (minValue: number, maxValue: number) => {
    if (filters.minBuyIn === minValue && filters.maxBuyIn === maxValue) {
      setFilters((prev) => ({
        ...prev,
        minBuyIn: undefined,
        maxBuyIn: undefined,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        minBuyIn: minValue,
        maxBuyIn: maxValue,
      }));
    }
  };

  const handleFullTables = () => {
    setFilters((prev) => ({
      ...prev,
      fullTables: !prev.fullTables,
    }));
  };

  const handleEmptyTables = () => {
    setFilters((prev) => ({
      ...prev,
      emptyTables: !prev.emptyTables,
    }));
  };

  const handleNameSearch = (name: string) => {
    dispatch(authActionCreator(AuthEvents.UPDATE_LOBBY_FILTERS, { name }));
    setFilters((prev) => ({
      ...prev,
      name: name,
    }));
  };

  const handleTableSize = (size: number) => {
    setFilters((prevFilters) => {
      const currentMaxPlayers = prevFilters.maxPlayers ? [...prevFilters.maxPlayers] : [];
      const isAlreadySelected = currentMaxPlayers.includes(size);

      return {
        ...prevFilters,
        maxPlayers: isAlreadySelected
          ? currentMaxPlayers.filter((playerSize) => playerSize !== size)
          : [...currentMaxPlayers, size],
      };
    });
  };

  const handleGameType = (gameType: BettingStructure) => {
    setFilters((prevFilters) => {
      const currentGameTypes = prevFilters.gameTypes ? [...prevFilters.gameTypes] : [];
      const isAlreadySelected = currentGameTypes.includes(gameType);

      return {
        ...prevFilters,
        gameTypes: isAlreadySelected
          ? currentGameTypes.filter((gameTypex) => gameTypex !== gameType)
          : [...currentGameTypes, gameType],
      };
    });
  };

  const handleStakes = (gameStake: StakeName) => {
    const stakeOptions: StakeOptionFilter[] = [];

    stakesOptions.forEach((betting) => {
      const matchingStakes = betting.stakes.filter((stake) => stake.stakeName === gameStake);
      if (matchingStakes.length > 0) {
        stakeOptions.push({
          bettingStructure: betting.bettingStructure,
          min: matchingStakes[0].range.min,
          max: matchingStakes[0].range.max,
        });
      }
    });

    setFilters((prev) => {
      const newStakeOptions = [...(prev.stakeOptions || [])];

      stakeOptions.forEach((stakeOption) => {
        const existingIndex = findStakeOptionIndex(newStakeOptions, stakeOption);

        if (existingIndex !== -1) {
          newStakeOptions.splice(existingIndex, 1);
        } else {
          newStakeOptions.push(stakeOption);
        }
      });

      return {
        ...prev,
        stakeOptions: [...newStakeOptions],
      };
    });
  };

  const findStakeOptionIndex = (options: StakeOptionFilter[], stakeOption: StakeOptionFilter) => {
    return options.findIndex(
      (option) =>
        option.min === stakeOption.min &&
        option.max === stakeOption.max &&
        option.bettingStructure === stakeOption.bettingStructure,
    );
  };

  const handleGames = (game: GameValue) => {
    setFilters((prevFilters) => {
      const currentGames = prevFilters.games ? [...prevFilters.games] : [];
      const isAlreadySelected = currentGames.some((gameItem) => isEqual(gameItem, game));

      return {
        ...prevFilters,
        games: isAlreadySelected
          ? currentGames.filter((gameValue) => !isEqual(gameValue, game))
          : [...currentGames, game],
      };
    });
  };

  const handleResetFilters = (): void => {
    setFilters(INITIAL_FILTERS);
  };

  return (
    <LobbyFiltersContext.Provider
      value={{
        filters,
        handleCategory,
        handleEmptyTables,
        handleNameSearch,
        handleFullTables,
        handleTableType,
        handleRangeChange,
        handleTableSize,
        handleStakes,
        handleResetFilters,
        handleGames,
        handleGameType,
        handleSaveFilters,
        handleCloseWithoutSaving,
      }}
    >
      {children}
    </LobbyFiltersContext.Provider>
  );
};

export default LobbyFiltersContext;
